import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Play, Info, PenLine, Triangle, Circle as CircleIcon, Dice5, Sigma
} from 'lucide-react';

const COLORS = ['#f43f5e','#8b5cf6','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4','#ef4444'];

const MISSIONS = [
  {
    id: 1, title: 'Parabola Explorer', difficulty: 1, type: 'quadratic',
    description: 'Adjust a, b, c sliders to shape the parabola y = ax² + bx + c. Hit the target zone!',
    params: { a: { min: -3, max: 3, step: 0.5, default: 1 }, b: { min: -5, max: 5, step: 0.5, default: 0 }, c: { min: -5, max: 5, step: 0.5, default: 0 } },
    target: { a: 1, b: 0, c: -2 },
    fact: 'Every parabola has an axis of symmetry at x = -b/2a. The vertex is the highest or lowest point!',
  },
  {
    id: 2, title: 'Linear Functions', difficulty: 1, type: 'linear',
    description: 'Adjust slope (m) and intercept (b) to match the target line y = mx + b.',
    params: { m: { min: -5, max: 5, step: 0.5, default: 1 }, b: { min: -5, max: 5, step: 0.5, default: 0 } },
    target: { m: 2, b: -1 },
    fact: 'The slope tells you rise over run. Parallel lines have equal slopes. Perpendicular lines have slopes that multiply to -1.',
  },
  {
    id: 3, title: 'Sine Wave Studio', difficulty: 2, type: 'trig',
    description: 'Adjust amplitude (A) and frequency (f) to match the target wave y = A·sin(f·x).',
    params: { A: { min: 0.5, max: 4, step: 0.5, default: 1 }, f: { min: 0.5, max: 4, step: 0.5, default: 1 } },
    target: { A: 2, f: 2 },
    fact: 'Sound, light, and radio are all waves. Musical pitch is determined by frequency — higher frequency = higher pitch!',
  },
  {
    id: 4, title: 'Shape Transformer', difficulty: 2, type: 'transform',
    description: 'Apply translation, rotation, and scaling transforms to match the target shape.',
    params: { tx: { min: -4, max: 4, step: 1, default: 0 }, ty: { min: -4, max: 4, step: 1, default: 0 }, rot: { min: 0, max: 360, step: 15, default: 0 }, scale: { min: 0.5, max: 2, step: 0.25, default: 1 } },
    target: { tx: 2, ty: 1, rot: 45, scale: 1.5 },
    fact: 'Transformations preserve shape properties. A rotation + translation is called a rigid motion — no stretching allowed!',
  },
  {
    id: 5, title: 'Symmetry Mirror', difficulty: 1, type: 'symmetry',
    description: 'Click grid cells on the left side. The right side mirrors automatically. Create the target pattern!',
    gridSize: 6,
    targetPattern: [[0,1],[0,2],[1,0],[1,1],[1,2],[2,0],[2,2]],
    fact: 'Symmetry appears everywhere in nature — butterfly wings, snowflakes, and even your face! Mathematicians study groups of symmetries.',
  },
  {
    id: 6, title: 'Coin Flip Simulator', difficulty: 1, type: 'coin',
    description: 'Flip a coin many times. Watch the ratio approach 50/50 as flips increase!',
    targetFlips: 50,
    fact: 'The Law of Large Numbers says that as experiments increase, the average result approaches the expected value. Flip enough coins and you get ~50% heads!',
  },
  {
    id: 7, title: 'Dice Probability', difficulty: 2, type: 'dice',
    description: 'Roll two dice many times. Discover which sum appears most often!',
    targetRolls: 40,
    fact: 'With two dice, 7 is the most likely sum (6 ways out of 36). The probability distribution forms a triangle shape!',
  },
  {
    id: 8, title: 'Distance & Midpoint', difficulty: 2, type: 'coordinate',
    description: 'Place two points on the grid. The distance and midpoint are computed live!',
    targetPoints: [{ x: 1, y: 1 }, { x: 4, y: 5 }],
    fact: 'Distance formula: √((x₂-x₁)² + (y₂-y₁)²). It comes from the Pythagorean theorem applied to the coordinate plane!',
  },
  {
    id: 9, title: 'Circle Equations', difficulty: 3, type: 'circle',
    description: 'Adjust center (h,k) and radius r to match the target circle: (x-h)²+(y-k)²=r².',
    params: { h: { min: -4, max: 4, step: 0.5, default: 0 }, k: { min: -4, max: 4, step: 0.5, default: 0 }, r: { min: 0.5, max: 4, step: 0.5, default: 1 } },
    target: { h: 2, k: -1, r: 3 },
    fact: 'The equation of a circle (x-h)²+(y-k)²=r² describes every point at distance r from center (h,k). Pi (π) is the ratio of circumference to diameter!',
  },
  {
    id: 10, title: 'Polynomial Roots', difficulty: 3, type: 'roots',
    description: 'Drag the roots (zeros) on the x-axis to shape the cubic curve. Match the target polynomial!',
    targetRoots: [-2, 1, 3],
    fact: 'A polynomial of degree n has exactly n roots (counting complex ones). The Fundamental Theorem of Algebra guarantees this!',
  },
];

/* ─── Helper: draw grid on canvas ─── */
function drawGrid(ctx, w, h, scale, ox, oy) {
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  for (let x = ox % scale; x < w; x += scale) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
  for (let y = oy % scale; y < h; y += scale) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, oy); ctx.lineTo(w, oy); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(ox, 0); ctx.lineTo(ox, h); ctx.stroke();
}

/* ─── Helper: plot function on canvas ─── */
function plotFunc(ctx, fn, w, h, scale, ox, oy, color, lineWidth = 2) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  let started = false;
  for (let px = 0; px < w; px++) {
    const x = (px - ox) / scale;
    const y = fn(x);
    const py = oy - y * scale;
    if (isNaN(py) || !isFinite(py)) { started = false; continue; }
    if (py < -1000 || py > h + 1000) { started = false; continue; }
    if (!started) { ctx.moveTo(px, py); started = true; } else ctx.lineTo(px, py);
  }
  ctx.stroke();
}

export default function MathVisualizerLab() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('math-visualizer', 10);
  const [score, setScore] = useState(0);
  const [params, setParams] = useState({});
  const [showFact, setShowFact] = useState(false);
  // Coin/Dice state
  const [coinResults, setCoinResults] = useState([]);
  const [diceResults, setDiceResults] = useState([]);
  const [flipping, setFlipping] = useState(false);
  // Symmetry state
  const [symCells, setSymCells] = useState(new Set());
  // Coordinate state
  const [coordPoints, setCoordPoints] = useState([]);
  // Roots state
  const [roots, setRoots] = useState([-3, 0, 2]);

  const mission = MISSIONS[currentMission];

  useEffect(() => {
    if (mission.params) {
      const p = {};
      Object.entries(mission.params).forEach(([k, v]) => { p[k] = v.default; });
      setParams(p);
    }
    setShowFact(false);
    setCoinResults([]);
    setDiceResults([]);
    setSymCells(new Set());
    setCoordPoints([]);
    setRoots(mission.type === 'roots' ? [-3, 0, 2] : []);
  }, [currentMission]);

  /* ─── Canvas rendering ─── */
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const scale = 40;
    const ox = w / 2, oy = h / 2;
    ctx.clearRect(0, 0, w, h);
    drawGrid(ctx, w, h, scale, ox, oy);

    if (mission.type === 'quadratic') {
      const { a = 1, b = 0, c = 0 } = params;
      plotFunc(ctx, x => a * x * x + b * x + c, w, h, scale, ox, oy, '#8b5cf6', 3);
      if (mission.target) {
        const t = mission.target;
        plotFunc(ctx, x => t.a * x * x + t.b * x + t.c, w, h, scale, ox, oy, 'rgba(16,185,129,0.4)', 2);
      }
    } else if (mission.type === 'linear') {
      const { m = 1, b = 0 } = params;
      plotFunc(ctx, x => m * x + b, w, h, scale, ox, oy, '#3b82f6', 3);
      if (mission.target) {
        const t = mission.target;
        plotFunc(ctx, x => t.m * x + t.b, w, h, scale, ox, oy, 'rgba(16,185,129,0.4)', 2);
      }
    } else if (mission.type === 'trig') {
      const { A = 1, f = 1 } = params;
      plotFunc(ctx, x => A * Math.sin(f * x), w, h, scale, ox, oy, '#f43f5e', 3);
      if (mission.target) {
        const t = mission.target;
        plotFunc(ctx, x => t.A * Math.sin(t.f * x), w, h, scale, ox, oy, 'rgba(16,185,129,0.4)', 2);
      }
    } else if (mission.type === 'circle') {
      const { h: ch = 0, k = 0, r = 1 } = params;
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(ox + ch * scale, oy - k * scale, r * scale, 0, Math.PI * 2);
      ctx.stroke();
      if (mission.target) {
        const t = mission.target;
        ctx.strokeStyle = 'rgba(16,185,129,0.4)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ox + t.h * scale, oy - t.k * scale, t.r * scale, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (mission.type === 'roots') {
      plotFunc(ctx, x => {
        let y = 0.3;
        roots.forEach(r => y *= (x - r));
        return y;
      }, w, h, scale, ox, oy, '#f59e0b', 3);
      if (mission.targetRoots) {
        plotFunc(ctx, x => {
          let y = 0.3;
          mission.targetRoots.forEach(r => y *= (x - r));
          return y;
        }, w, h, scale, ox, oy, 'rgba(16,185,129,0.4)', 2);
      }
      roots.forEach((r, i) => {
        ctx.fillStyle = COLORS[i % COLORS.length];
        ctx.beginPath();
        ctx.arc(ox + r * scale, oy, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(r.toFixed(1), ox + r * scale, oy + 20);
      });
    } else if (mission.type === 'transform') {
      const { tx = 0, ty = 0, rot = 0, scale: sc = 1 } = params;
      // Target shape (green ghost)
      const t = mission.target;
      ctx.save();
      ctx.translate(ox + t.tx * scale, oy - t.ty * scale);
      ctx.rotate(-t.rot * Math.PI / 180);
      ctx.scale(t.scale, t.scale);
      ctx.strokeStyle = 'rgba(16,185,129,0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-20, 20); ctx.lineTo(20, 20); ctx.lineTo(0, -20); ctx.closePath();
      ctx.stroke();
      ctx.restore();
      // User shape
      ctx.save();
      ctx.translate(ox + tx * scale, oy - ty * scale);
      ctx.rotate(-rot * Math.PI / 180);
      ctx.scale(sc, sc);
      ctx.fillStyle = 'rgba(139,92,246,0.3)';
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(-20, 20); ctx.lineTo(20, 20); ctx.lineTo(0, -20); ctx.closePath();
      ctx.fill(); ctx.stroke();
      ctx.restore();
    } else if (mission.type === 'coordinate') {
      coordPoints.forEach((pt, i) => {
        const px = ox + pt.x * scale;
        const py = oy - pt.y * scale;
        ctx.fillStyle = COLORS[i];
        ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`(${pt.x},${pt.y})`, px, py - 12);
      });
      if (coordPoints.length === 2) {
        const [a, b] = coordPoints;
        ctx.strokeStyle = '#f43f5e';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(ox + a.x * scale, oy - a.y * scale);
        ctx.lineTo(ox + b.x * scale, oy - b.y * scale);
        ctx.stroke();
        ctx.setLineDash([]);
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        ctx.fillStyle = '#10b981';
        ctx.beginPath(); ctx.arc(ox + mx * scale, oy - my * scale, 5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(`M(${mx.toFixed(1)},${my.toFixed(1)})`, ox + mx * scale, oy - my * scale - 10);
      }
    }
  }, [params, mission, roots, coordPoints]);

  useEffect(() => { renderCanvas(); }, [renderCanvas]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const px = (e.clientX - rect.left) * sx;
    const py = (e.clientY - rect.top) * sx;
    const scale = 40;
    const ox = canvas.width / 2, oy = canvas.height / 2;
    const gx = Math.round((px - ox) / scale);
    const gy = Math.round((oy - py) / scale);

    if (mission.type === 'coordinate' && coordPoints.length < 2) {
      setCoordPoints(prev => [...prev, { x: gx, y: gy }]);
    }
    if (mission.type === 'roots') {
      const closest = roots.reduce((best, r, i) => {
        const d = Math.abs(ox + r * scale - px);
        return d < best.d ? { d, i } : best;
      }, { d: Infinity, i: -1 });
      if (closest.d < 20) {
        const newRoots = [...roots];
        newRoots[closest.i] = gx;
        setRoots(newRoots);
      }
    }
  };

  const checkMatch = () => {
    if (mission.type === 'quadratic' || mission.type === 'linear' || mission.type === 'trig' || mission.type === 'circle') {
      const target = mission.target;
      return Object.keys(target).every(k => Math.abs((params[k] || 0) - target[k]) < 0.3);
    }
    if (mission.type === 'transform') {
      const t = mission.target;
      return Math.abs(params.tx - t.tx) <= 1 && Math.abs(params.ty - t.ty) <= 1 &&
        (Math.abs(params.rot - t.rot) <= 15 || Math.abs(params.rot - t.rot - 360) <= 15) &&
        Math.abs(params.scale - t.scale) <= 0.25;
    }
    if (mission.type === 'roots') {
      const t = [...mission.targetRoots].sort((a, b) => a - b);
      const r = [...roots].sort((a, b) => a - b);
      return t.every((v, i) => Math.abs(v - r[i]) <= 0.5);
    }
    if (mission.type === 'coordinate') {
      return coordPoints.length === 2;
    }
    if (mission.type === 'symmetry') {
      const t = new Set(mission.targetPattern.map(([r, c]) => `${r},${c}`));
      return t.size === symCells.size && [...t].every(k => symCells.has(k));
    }
    if (mission.type === 'coin') return coinResults.length >= mission.targetFlips;
    if (mission.type === 'dice') return diceResults.length >= mission.targetRolls;
    return false;
  };

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + (mission.difficulty * 10));
    }
    setShowFact(true);
  };

  const flipCoin = () => {
    setFlipping(true);
    setTimeout(() => {
      setCoinResults(prev => [...prev, Math.random() < 0.5 ? 'H' : 'T']);
      setFlipping(false);
    }, 300);
  };

  const rollDice = () => {
    setFlipping(true);
    setTimeout(() => {
      const d1 = Math.ceil(Math.random() * 6);
      const d2 = Math.ceil(Math.random() * 6);
      setDiceResults(prev => [...prev, d1 + d2]);
      setFlipping(false);
    }, 300);
  };

  const toggleSymCell = (r, c) => {
    const key = `${r},${c}`;
    setSymCells(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const heads = coinResults.filter(r => r === 'H').length;
  const tails = coinResults.length - heads;

  /* ─── Mission Selector ─── */
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><PenLine className="w-5 h-5 text-violet-400" /> Math Visualizer</h1>
            <Badge variant="secondary" className="bg-violet-500/20 text-violet-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose a Mission</h2>
            <p className="text-sm text-muted-foreground">Explore math visually — graphs, shapes, probability & more!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:border-violet-500/30 hover:bg-violet-500/5'
                  }`}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] text-foreground uppercase tracking-widest">Mission {idx + 1}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  /* ─── Lab View ─── */
  const matched = checkMatch();
  const isCompleted = completedMissions.includes(currentMission);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Missions
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="math-bench">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Left: Canvas / Interactive Area */}
          <div className="space-y-4">
            {/* Canvas-based missions */}
            {['quadratic','linear','trig','transform','circle','roots','coordinate'].includes(mission.type) && (
              <div className="rounded-2xl border border-border bg-card p-2 overflow-hidden" data-testid="graph-area">
                <canvas ref={canvasRef} width={600} height={400} onClick={handleCanvasClick}
                  className="w-full rounded-xl bg-[#0d0d14] cursor-crosshair" data-testid="math-canvas" />
                <div className="flex items-center gap-3 mt-2 px-2 text-[10px] text-muted-foreground/60">
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-violet-500 inline-block rounded" /> Your curve</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500/50 inline-block rounded" /> Target</span>
                </div>
              </div>
            )}

            {/* Sliders for param-based missions */}
            {mission.params && (
              <div className="grid grid-cols-2 gap-3 p-4 rounded-2xl border border-border bg-card" data-testid="param-panel">
                {Object.entries(mission.params).map(([key, cfg]) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground flex justify-between mb-1">
                      <span>{key}</span><span className="text-foreground font-mono">{params[key] ?? cfg.default}</span>
                    </label>
                    <input type="range" min={cfg.min} max={cfg.max} step={cfg.step}
                      value={params[key] ?? cfg.default}
                      onChange={e => setParams(p => ({ ...p, [key]: parseFloat(e.target.value) }))}
                      className="w-full accent-violet-500" data-testid={`slider-${key}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Symmetry Grid */}
            {mission.type === 'symmetry' && (
              <div className="p-4 rounded-2xl border border-border bg-card" data-testid="symmetry-grid">
                <p className="text-xs text-muted-foreground/70 mb-3">Click left-side cells. Right side mirrors automatically!</p>
                <div className="inline-grid gap-1" style={{ gridTemplateColumns: `repeat(${mission.gridSize * 2}, 28px)` }}>
                  {Array.from({ length: mission.gridSize }).map((_, r) =>
                    Array.from({ length: mission.gridSize * 2 }).map((_, c) => {
                      const isLeft = c < mission.gridSize;
                      const mappedC = isLeft ? c : mission.gridSize * 2 - 1 - c;
                      const key = `${r},${mappedC}`;
                      const active = symCells.has(key);
                      const isMirrorLine = c === mission.gridSize - 1 || c === mission.gridSize;
                      return (
                        <button key={`${r}-${c}`}
                          onClick={() => isLeft && toggleSymCell(r, c)}
                          className={`w-7 h-7 rounded-md border transition-all ${
                            active ? 'bg-violet-500 border-violet-400' :
                            isMirrorLine ? 'bg-muted/50 border-white/20' :
                            'bg-card border-border hover:border-border'
                          } ${!isLeft ? 'cursor-default' : 'cursor-pointer'}`}
                        />
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Coin Flip */}
            {mission.type === 'coin' && (
              <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="coin-area">
                <div className="flex items-center gap-6">
                  <motion.div animate={flipping ? { rotateY: 360 } : { rotateY: 0 }} transition={{ duration: 0.3 }}
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-2xl font-bold text-white shadow-xl" data-testid="coin">
                    {coinResults.length > 0 ? coinResults[coinResults.length - 1] : '?'}
                  </motion.div>
                  <div>
                    <Button onClick={flipCoin} disabled={flipping} className="bg-amber-500 hover:bg-amber-600" data-testid="flip-btn">
                      <Play className="w-4 h-4 mr-1" /> Flip Coin
                    </Button>
                    <p className="text-xs text-muted-foreground/70 mt-2">Flips: {coinResults.length} / {mission.targetFlips}</p>
                  </div>
                </div>
                {coinResults.length > 0 && (
                  <div className="flex items-end gap-1 h-20">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-16 bg-blue-500/80 rounded-t" style={{ height: `${(heads / coinResults.length) * 60}px` }} />
                      <span className="text-xs text-muted-foreground">H: {heads} ({((heads / coinResults.length) * 100).toFixed(0)}%)</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-16 bg-rose-500/80 rounded-t" style={{ height: `${(tails / coinResults.length) * 60}px` }} />
                      <span className="text-xs text-muted-foreground">T: {tails} ({((tails / coinResults.length) * 100).toFixed(0)}%)</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dice Roll */}
            {mission.type === 'dice' && (
              <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="dice-area">
                <div className="flex items-center gap-6">
                  <motion.div animate={flipping ? { rotate: 360 } : {}} transition={{ duration: 0.3 }}
                    className="flex gap-2">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-xl font-bold shadow-xl" data-testid="die-1">
                      {diceResults.length > 0 ? Math.ceil(Math.random() * 6) : '?'}
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-xl font-bold shadow-xl" data-testid="die-2">
                      {diceResults.length > 0 ? Math.ceil(Math.random() * 6) : '?'}
                    </div>
                  </motion.div>
                  <div>
                    <Button onClick={rollDice} disabled={flipping} className="bg-rose-500 hover:bg-rose-600" data-testid="roll-btn">
                      <Dice5 className="w-4 h-4 mr-1" /> Roll Dice
                    </Button>
                    <p className="text-xs text-muted-foreground/70 mt-2">Rolls: {diceResults.length} / {mission.targetRolls}</p>
                  </div>
                </div>
                {diceResults.length > 0 && (
                  <div className="flex items-end gap-1 h-24">
                    {Array.from({ length: 11 }, (_, i) => i + 2).map(sum => {
                      const count = diceResults.filter(r => r === sum).length;
                      return (
                        <div key={sum} className="flex flex-col items-center gap-1 flex-1">
                          <div className="w-full bg-violet-500/60 rounded-t transition-all" style={{ height: `${(count / diceResults.length) * 80}px` }} />
                          <span className="text-[9px] text-muted-foreground">{sum}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Roots: drag instructions */}
            {mission.type === 'roots' && (
              <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/5">
                <p className="text-xs text-amber-300">Click near a root dot on the x-axis, then click a new position to drag it. Match the green target curve!</p>
              </div>
            )}
            {mission.type === 'coordinate' && (
              <div className="p-3 rounded-xl border border-blue-500/20 bg-blue-500/5">
                <p className="text-xs text-blue-300">Click on the grid to place {2 - coordPoints.length} more point{2 - coordPoints.length !== 1 ? 's' : ''}. Distance & midpoint computed live!</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              {matched && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> {mission.type === 'coin' || mission.type === 'dice' ? 'Record Observation' : 'Match Found!'} 
                </Button>
              )}
              <Button variant="outline" onClick={() => {
                if (mission.params) {
                  const p = {};
                  Object.entries(mission.params).forEach(([k, v]) => { p[k] = v.default; });
                  setParams(p);
                }
                setCoinResults([]); setDiceResults([]); setSymCells(new Set()); setCoordPoints([]); setRoots([-3, 0, 2]);
                setShowFact(false);
              }} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>

            {/* Fact */}
            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl border border-violet-500/20 bg-violet-500/5" data-testid="math-fact">
                  <p className="text-xs text-violet-400 font-semibold flex items-center gap-1 mb-1"><Info className="w-3 h-3" /> Did You Know?</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); setShowFact(false); }}
                      className="mt-3 bg-violet-600 hover:bg-violet-500" data-testid="next-mission-btn">
                      Next Mission <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Mission Info */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Sigma className="w-4 h-4 text-violet-400" /> Objective</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); setShowFact(false); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
